import { ArrowLeft, ArrowRight, Trash } from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  addAchievement,
  deleteAchievement
} from '../../redux/Slices/ResumeSlice';
import Button from '../Common/Button';

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
};

type Achievement = {
  title: string;
  issuer: string;
  year: number;
  description?: string;
};

const AchievementsForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  prevStepName,
  onNext,
  onBack
}) => {
  const dispatch = useDispatch();

  const achievements: Achievement[] = useSelector(
    (state: any) => state.root.resume.achievements
  );

  const TITLE_LIMIT = 50;
  const ISSUER_LIMIT = 50;
  const DESC_LIMIT = 200;
  const YEAR_LIMIT = 4;

  const [form, setForm] = useState({
    title: '',
    issuer: '',
    year: '',
    description: ''
  });

  const [errors, setErrors] = useState({
    title: '',
    issuer: '',
    year: '',
    description: ''
  });

  /* changed to normal function */
  function CharCount(value: string, max: number) {
    const len = value?.length || 0;

    return (
      <span
        className={`text-xs ${len >= max ? 'text-red-500' : 'text-gray-400'}`}
      >
        {len}/{max}
      </span>
    );
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'title' && value.length > TITLE_LIMIT) return;

    if (name === 'issuer' && value.length > ISSUER_LIMIT) return;

    if (name === 'description' && value.length > DESC_LIMIT) return;

    if (name === 'year') {
      if (!/^[0-9]*$/.test(value)) {
        setErrors(prev => ({
          ...prev,
          year: 'Year must contain only numbers'
        }));

        return;
      }

      if (value.length > YEAR_LIMIT) return;
    }

    setForm({
      ...form,
      [name]: value
    });

    let message = '';

    if (!value.trim() && name !== 'description') {
      message = `${name} is required`;
    }

    if (name === 'year' && value.length !== 4) {
      message = 'Year must be 4 digits';
    }

    if (name === 'title' && value.length === TITLE_LIMIT) {
      message = 'Max 50 characters reached';
    }

    if (name === 'issuer' && value.length === ISSUER_LIMIT) {
      message = 'Max 50 characters reached';
    }

    if (name === 'description' && value.length === DESC_LIMIT) {
      message = 'Max 200 characters reached';
    }

    setErrors(prev => ({
      ...prev,
      [name]: message
    }));
  };

  const isSaveValid =
    form.title.trim() &&
    form.issuer.trim() &&
    form.year.length === 4 &&
    /^[0-9]{4}$/.test(form.year);

  const saveAchievement = () => {
    if (!isSaveValid) return;

    dispatch(
      addAchievement({
        ...form,
        year: Number(form.year)
      })
    );

    setForm({
      title: '',
      issuer: '',
      year: '',
      description: ''
    });

    setErrors({
      title: '',
      issuer: '',
      year: '',
      description: ''
    });
  };

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>

            <h1 className='text-2xl font-semibold'>{stepName}</h1>

            <p className='mt-1 text-sm text-gray-400'>
              Highlight your awards and recognitions
            </p>
          </div>

          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{
              width: `${((step - 1) / totalSteps) * 100}%`
            }}
          />
        </div>

        <div className='rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>+ Add New Achievement</h3>

          <div>
            <div className='flex justify-between'>
              <label className='text-sm font-medium'>
                Achievement Title <span className='text-red-500'>*</span>
              </label>

              {CharCount(form.title, TITLE_LIMIT)}
            </div>

            <input
              name='title'
              maxLength={TITLE_LIMIT}
              value={form.title}
              onChange={handleChange}
              placeholder='Enter title'
              className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
              ${errors.title ? 'border-red-500' : 'border-gray-300'}
              `}
            />

            {errors.title && (
              <p className='mt-1 text-xs text-red-500'>{errors.title}</p>
            )}
          </div>

          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <div className='flex justify-between'>
                <label className='text-sm font-medium'>
                  Issuer <span className='text-red-500'>*</span>
                </label>

                {CharCount(form.issuer, ISSUER_LIMIT)}
              </div>

              <input
                name='issuer'
                maxLength={ISSUER_LIMIT}
                value={form.issuer}
                onChange={handleChange}
                placeholder='Enter issuer'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
                ${errors.issuer ? 'border-red-500' : 'border-gray-300'}
                `}
              />

              {errors.issuer && (
                <p className='mt-1 text-xs text-red-500'>{errors.issuer}</p>
              )}
            </div>

            <div>
              <div className='flex justify-between'>
                <label className='text-sm font-medium'>
                  Year <span className='text-red-500'>*</span>
                </label>

                {CharCount(form.year, YEAR_LIMIT)}
              </div>

              <input
                name='year'
                maxLength={YEAR_LIMIT}
                value={form.year}
                onChange={handleChange}
                placeholder='YYYY'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
                ${errors.year ? 'border-red-500' : 'border-gray-300'}
                `}
              />

              {errors.year && (
                <p className='mt-1 text-xs text-red-500'>{errors.year}</p>
              )}
            </div>
          </div>

          <div className='mt-4'>
            <div className='flex justify-between'>
              <label className='text-sm font-medium'>Description</label>

              {CharCount(form.description, DESC_LIMIT)}
            </div>

            <textarea
              name='description'
              maxLength={DESC_LIMIT}
              value={form.description}
              onChange={handleChange}
              placeholder='Enter description'
              rows={3}
              className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
              ${errors.description ? 'border-red-500' : 'border-gray-300'}
              `}
            />

            {errors.description && (
              <p className='mt-1 text-xs text-red-500'>{errors.description}</p>
            )}
          </div>

          <Button
            label='+ Save Achievement'
            handleClickBtn={saveAchievement}
            variant='contained'
            disabled={!isSaveValid}
            classes='mt-5'
          />
        </div>

        <div className='mt-6 rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>Added Achievements</h3>

          {achievements.length === 0 ? (
            <p className='text-sm text-gray-400'>No achievements added yet.</p>
          ) : (
            <div className='space-y-3'>
              {achievements.map((item: Achievement) => (
                <div
                  key={item.title}
                  className='flex items-center justify-between rounded-lg border p-3'
                >
                  <div>
                    <p className='font-medium'>{item.title}</p>

                    <p className='text-xs text-gray-500'>
                      {item.issuer} • {item.year}
                    </p>
                  </div>

                  <button
                    onClick={() => dispatch(deleteAchievement(item.title))}
                    className='text-xs text-red-500'
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='mt-10 flex items-center justify-between'>
          <Button
            label={`Previous: ${prevStepName}`}
            handleClickBtn={onBack}
            variant='outlined'
          >
            <ArrowLeft size={16} />
          </Button>

          <Button
            label='Continue'
            handleClickBtn={onNext}
            variant='contained'
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AchievementsForm;
